<script lang="ts" setup>
import { DownloadIcon } from "@frontiers/brink-icons";
import {
  CustomLink,
  LinkIcon,
  LinkIconLocation,
  LinkColor,
  FrontiersIcon,
  FrontiersImage
} from "@frontiers-vue3/public-pages";
import type { IArticle } from "~/types";
import { isoToDateConverter } from "~/helpers";
import { numberCommaSeparator } from "~/helpers/ChartData.helpers";

const props = defineProps<{
  article: IArticle;
  showViews?: boolean;
}>();

const {
  public: { baseUrl, loopUrl }
} = useRuntimeConfig();
const loopImpactBaseUrl = loopUrl.replace("loop.", "loop-impact.");

const publishedDate = computed(() =>
  props.article.publishedOn ? isoToDateConverter(props.article.publishedOn) : ""
);

const articleUrl = computed(
  () =>
    `${baseUrl}/journals/${props.article.journal?.journalSlug}/articles/${props.article.doi}/full`
);

const downloadArticleUrl = computed(
  () =>
    `${baseUrl}/journals/${props.article.journal?.journalSlug}/articles/${props.article.doi}/pdf`
);

const impactUrl = computed(
  () =>
    `${loopImpactBaseUrl}/impact/article/${props.article.id}#totalviews/views`
);

const views = computed(() => props.article.impactV5?.views || 0);
const citations = computed(() => props.article.impactV5?.citations || 0);

const seeMoreLink = computed(() => ({
  text: "Read the full article",
  url: articleUrl.value,
  target: "_blank"
}));
</script>
<template>
  <article class="ArticleCard">
    <div class="ArticleCard__main">
      <div class="ArticleCard__content">
        <p class="ArticleCard__header">
          <span class="ArticleCard__type">{{
            article.articleType?.toUpperCase()
          }}</span>
          <span
            v-if="publishedDate"
            class="ArticleCard__date"
            >Published on {{ publishedDate }}</span
          >
        </p>
        <h3 class="ArticleCard__title">{{ article.title }}</h3>
        <ul class="ArticleCard__authors">
          <li
            v-for="author in article.authors"
            :key="author.userId">
            <p class="ArticleCard__author">{{ author.fullName }}</p>
          </li>
        </ul>

        <div class="ArticleCard__stats">
          <p
            v-if="showViews && views > 0"
            class="ArticleCard__stat">
            <strong>{{ numberCommaSeparator(views) }}</strong> views
          </p>
          <p
            v-if="citations > 0"
            class="ArticleCard__stat">
            <strong>{{ numberCommaSeparator(citations) }}</strong> citations
          </p>
        </div>
      </div>
      <FrontiersImage
        v-if="article.imageUrl"
        class="ArticleCard__image"
        :sources="[article.imageUrl]"
        :alt="article.title"
        loading="lazy" />
    </div>

    <div class="ArticleCard__actions">
      <CustomLink
        class="ArticleCard__seeMore"
        :link="seeMoreLink"
        :color="LinkColor.Grey"
        :icon="LinkIcon.ChevronRight"
        :icon-location="LinkIconLocation.Right" />
      <div class="ArticleCard__links">
        <CustomLink
          :link="{ text: 'Impact', url: impactUrl, target: '_blank' }"
          :color="LinkColor.Grey"
          :icon="FrontiersIcon.ExternalLink"
          :icon-location="LinkIconLocation.Right" />
        <a
          :href="downloadArticleUrl"
          class="ArticleCard__link ArticleCard__link--icon"
          aria-label="Download PDF">
          <DownloadIcon size="md" />
        </a>
      </div>
    </div>
  </article>
</template>
<style lang="scss">
.ArticleCard {
  display: flex;
  flex-direction: column;
  gap: spacing(1);
  padding: spacing(4) 0;
  border-bottom: 1px solid $grey30;

  &__main {
    display: flex;
    gap: spacing(6);
  }

  &__content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: spacing(1);
  }

  &__header {
    display: flex;
    align-items: center;
  }

  &__type {
    @include f(heading-xs-bold);
    color: $textPrimary;
  }

  &__date {
    @include f(body-sm);
    color: $textTertiary;
    margin-left: spacing(2);
    padding-left: spacing(2);
    border-left: 1px solid $grey40;
  }

  &__title {
    @include f(fluid-heading-md);
    color: $textPrimary;
    text-decoration: none;
  }

  &__authors {
    li {
      display: inline;

      p {
        &:after {
          content: " · ";
          margin-left: spacing(1);
          margin-right: spacing(1);
        }
      }

      &:last-child {
        p {
          &:after {
            content: "";
          }
        }
      }
    }
  }

  &__author {
    @include f(body-sm);
    color: $textSecondary;
    display: inline;
  }

  &__authorSeparator {
    color: $textTertiary;
  }

  &__stats {
    display: flex;
    gap: spacing(3);
  }

  &__stat {
    @include f-12;
    color: $textTertiary;

    strong {
      font-weight: 700;
    }
  }

  &__actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: spacing(1);
  }

  &__seeMore {
    flex-shrink: 0;
  }

  &__links {
    display: flex;
    align-items: center;
    gap: spacing(3);
    margin-left: auto;
  }

  &__link {
    @include f(body-sm);
    display: flex;
    align-items: center;
    gap: spacing(1);
    color: $blue50;
    text-decoration: none;

    &:hover {
      color: $blue70;

      svg {
        fill: $blue70;
      }
    }

    svg {
      fill: $blue50;
    }

    &--icon {
      color: $textTertiary;

      svg {
        fill: $textTertiary;
      }

      &:hover {
        color: $textPrimary;

        svg {
          fill: $textPrimary;
        }
      }
    }
  }

  &__image {
    width: 160px;
    flex-shrink: 0;

    @media #{$break-xs} {
      display: none;
    }

    img {
      width: 100%;
      height: auto;
      border-radius: spacing(1);
      object-fit: cover;
    }
  }
}
</style>
